import { z } from 'zod';

/**
 * The dispatch board's bottom pane: the four work queues the office clears (114 part 5).
 *
 * ⭐ These are NOT filters over the day on the board above. The board answers "who goes
 * where today"; these answer "what is not on a board yet, and what is stuck". A job with no
 * visit does not appear on ANY day's board — which is exactly why it needs a queue of its
 * own — and an unassigned visit two Tuesdays ago is the one most worth surfacing, not the
 * one to age out at midnight.
 *
 * ⚠️ The reference's five tabs are not these four. `113/DECISIONS.md` walks the mapping:
 * "Follow Up" was STRIPPED (no flag, no state, nothing to filter on), and **Requests** —
 * which the reference has no tab for — is ServWell's real top of funnel. "Partially
 * completed" is the fifth. It was absent until 2026-08-27 because its approved definition is
 * checklist complete AND customer signed off, and customer sign-off did not exist in the
 * schema until ticket 118 built it — a tab reading only the checklist half would have shipped
 * the weaker definition under the stronger one's name. **118 shipped `job_signoffs`, so the
 * tab is now built on both halves.**
 *
 * ⚠️ **The definition needed one qualifier the approved wording does not carry.** Giuliano's
 * words were *"a job is 100% complete when every checklist item is complete and the customer
 * has signed off. Anything short of that, on an open job, is partially completed."* Read
 * literally, a job created five minutes ago is "partially completed" — no ticks, no
 * signature — and the tab becomes a duplicate of every open job, overlapping `unscheduled`
 * and `unassigned` entirely.
 *
 * So the queue is scoped to jobs where **work has actually begun** (`in_progress` /
 * `work_done`). That is the reading that makes it a DISPATCH queue like its four siblings:
 * each of them names something dispatch must act on, and this one names *the crew left it
 * unfinished — book the return*. Recorded here rather than assumed silently.
 */
export const DISPATCH_QUEUES = [
  'unscheduled',
  'unassigned',
  'on_hold',
  'partially_completed',
  'requests',
] as const;
export type DispatchQueue = (typeof DISPATCH_QUEUES)[number];

export const dispatchQueuesInput = z.object({
  queue: z.enum(DISPATCH_QUEUES),
  /** One pane's worth. The counts are exact regardless — see `DispatchQueues.counts`. */
  limit: z.number().int().min(1).max(100).default(25),
});
export type DispatchQueuesInput = z.infer<typeof dispatchQueuesInput>;

export interface DispatchQueueRow {
  /** Stable row key — the visit, job or request id, whichever this queue is made of. */
  key: string;
  /** What clicking it opens. A request has no job yet, which is the point of the queue. */
  jobId: string | null;
  visitId: string | null;
  /** "#1042" for a job, "R-17" for a request. */
  ref: string;
  title: string;
  customer: string;
  address: string | null;
  status: string;
  /**
   * The instant this queue sorts and reports on — created, scheduled or last touched. What
   * it MEANS differs per queue, so the column header names it rather than the row carrying
   * a label that would be identical on every row of a tab.
   */
  at: Date | null;
}

export interface DispatchQueues {
  /**
   * Every queue's size, on every call.
   *
   * ⚠️ Exact, and never derived from `rows.length` — the rows are capped at `limit` and a
   * count read off a capped list is the "capped list must state its total" failure with the
   * cap hidden. A tab reading "12" over a list of 25 rows is a tab that is lying by 13.
   */
  counts: Record<DispatchQueue, number>;
  /** The selected queue's rows, oldest-need-first, capped at `limit`. */
  rows: DispatchQueueRow[];
  /** How many of the selected queue's rows the cap left out. */
  more: number;
}
